/**
 * KeywordSelector — shared component for selecting or entering a primary keyword.
 *
 * Used in both the creation pipeline (Stage2aSerpV2) and refresh pipeline (RefreshSerpV2).
 * Shows the current keyword, a dropdown with existing options + "Other...", and a
 * custom input form for entering a new keyword with optional volume/KD.
 */

import React, { useCallback, useState } from 'react';
import { useArticleContext } from '../ArticleContext';
import { blogRunsService } from '../../../services/blogRunsService';

interface KeywordOption {
  keyword: string;
  volume?: number | null;
  kd?: number | null;
  rationale?: string;
}

interface KeywordSelectorProps {
  /** List of keyword options to show in the dropdown (from LLM or audit). */
  options: KeywordOption[];
  /** Additional label text for the SEO signal badge. */
  seoSignal?: string | null;
}

export function KeywordSelector({ options, seoSignal }: KeywordSelectorProps) {
  const { document, run, refreshDocument } = useArticleContext();
  const kw = document?.targeting?.keywords;
  const primaryKeyword = kw?.primary?.keyword;
  const hasPrimary = Boolean(primaryKeyword);

  const primaryIsInOptions = options.some(
    (o) => o.keyword === primaryKeyword
  );

  // Auto-show custom input when there are no options and no keyword (Other... is the only choice)
  const [showCustomInput, setShowCustomInput] = useState(!hasPrimary && options.length === 0);
  const [customKeyword, setCustomKeyword] = useState('');
  const [customVolume, setCustomVolume] = useState('');
  const [customKd, setCustomKd] = useState('');
  const [isChanging, setIsChanging] = useState(false);

  const applyKeyword = useCallback(async (keyword: string, volume?: number | null, kd?: number | null) => {
    if (!run?.run_id) return;
    setIsChanging(true);
    try {
      await blogRunsService.updateDocument(run.run_id, {
        targeting: {
          keywords: {
            primary: { keyword, volume: volume ?? null, kd: kd ?? null },
          },
          serp: {},
          competitors: { urls: [] },
        },
      });
      setShowCustomInput(false);
      setCustomKeyword('');
      setCustomVolume('');
      setCustomKd('');
      await refreshDocument();
    } finally {
      setIsChanging(false);
    }
  }, [run?.run_id, refreshDocument]);

  const handleDropdownChange = useCallback((value: string) => {
    if (value === '__custom__') {
      setShowCustomInput(true);
      return;
    }
    if (value === '__current__') return;
    const opt = options.find(o => o.keyword === value);
    if (opt) {
      applyKeyword(value, opt.volume, opt.kd);
    } else {
      applyKeyword(value);
    }
  }, [options, applyKeyword]);

  const handleConfirmCustom = useCallback(async () => {
    const trimmed = customKeyword.trim();
    if (!trimmed) return;
    const vol = customVolume.trim() ? Number(customVolume) : null;
    const kd = customKd.trim() ? Number(customKd) : null;
    await applyKeyword(trimmed, vol, kd);
  }, [customKeyword, customVolume, customKd, applyKeyword]);

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Primary Keyword</div>
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-gray-900">
              {primaryKeyword || <span className="text-red-500 text-sm">No primary keyword set</span>}
            </span>
            {hasPrimary && (
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                title="Copy keyword"
                onClick={() => navigator.clipboard.writeText(primaryKeyword!)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>
              </button>
            )}
          </div>
          {kw?.primary?.volume != null && (
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
              <span>Vol: <strong>{kw.primary.volume}</strong></span>
              {kw.primary.kd != null && <span>KD: <strong>{kw.primary.kd}</strong></span>}
            </div>
          )}
        </div>
        {seoSignal && (
          <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${
            seoSignal === 'strong' ? 'bg-green-100 text-green-700'
            : seoSignal === 'moderate' ? 'bg-amber-100 text-amber-700'
            : 'bg-red-100 text-red-700'
          }`}>
            SEO: {seoSignal}
          </span>
        )}
      </div>

      {/* Dropdown + custom input */}
      <div className="mt-3 pt-3 border-t border-gray-200">
        <label className="text-xs text-gray-500">
          Choose a different primary keyword:
          <select
            className="ml-2 border border-gray-300 rounded px-2 py-1 text-sm text-gray-700 bg-white"
            value={showCustomInput ? '__custom__' : primaryIsInOptions ? primaryKeyword : '__current__'}
            onChange={(e) => handleDropdownChange(e.target.value)}
            disabled={isChanging}
          >
            {!primaryIsInOptions && primaryKeyword && (
              <option value="__current__">{primaryKeyword} (custom)</option>
            )}
            {options.map((opt, i) => (
              <option key={i} value={opt.keyword}>
                {opt.keyword}
                {opt.volume != null ? ` (Vol: ${opt.volume}` : ''}
                {opt.kd != null ? `, KD: ${opt.kd})` : opt.volume != null ? ')' : ''}
              </option>
            ))}
            <option value="__custom__">Other...</option>
          </select>
        </label>

        {showCustomInput && (
          <div className="mt-3 flex items-end gap-2">
            <div className="flex-1">
              <label className="text-xs text-gray-500">Keyword</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                value={customKeyword}
                onChange={(e) => setCustomKeyword(e.target.value)}
                placeholder="Enter keyword..."
                autoFocus
              />
            </div>
            <div className="w-20">
              <label className="text-xs text-gray-500">Volume</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                value={customVolume}
                onChange={(e) => setCustomVolume(e.target.value)}
                placeholder="—"
              />
            </div>
            <div className="w-20">
              <label className="text-xs text-gray-500">KD</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                value={customKd}
                onChange={(e) => setCustomKd(e.target.value)}
                placeholder="—"
              />
            </div>
            <button
              type="button"
              className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
              onClick={handleConfirmCustom}
              disabled={!customKeyword.trim() || isChanging}
            >
              {isChanging ? 'Updating...' : 'Set'}
            </button>
            <button
              type="button"
              className="px-2 py-1 text-sm text-gray-500 hover:text-gray-700"
              onClick={() => setShowCustomInput(false)}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
